import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { roundPlanningType, rolesObjType } from "../../../types/api";
import {
  useOrgUsersStore,
  usePremisesStore,
  useRoleManageStore,
  ROUND_PLANNING_AREAS,
  useDepartmentStore,
} from "../../../store/store";
import { MultiSelectDropdown } from "../../../components/dropdown/MultiSelectDropdown";
import { useSafetyFacilityRoundsStore } from "../../../store/SafetyFacilityRoundsStore";
import { useRoundTypesStore } from "../../../store/masterDataStore";
import { usePermissions } from "../../../hooks/usePermission";

interface AddRoundPlanningProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: roundPlanningType) => void;
  mode: "create" | "edit" | "view";
  initialData?: roundPlanningType | null;
}

const frequencyOptions = ["Daily", "Weekly", "Monthly", "Quarterly", "Yearly"];

const AddRoundPlanning: React.FC<AddRoundPlanningProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  initialData,
}) => {
  const [formData, setFormData] = useState<roundPlanningType>({
    roundName: "",
    roundType: "",
    Frequency: "",
    Premise: "",
    Area: [],
    assignedRole: "",
    checklistTemplate: "",
    status: "Scheduled",
    sla: "",
    escalationMatrix: "",
  });
  const [validationError, setValidationError] = useState<string>("");
  const { canCreate, canRead, canDelete, canUpdate } = usePermissions();
  // stores
  const { checklists, fetchChecklists } = useSafetyFacilityRoundsStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { rolesData, fetchAllRoles } = useRoleManageStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { roundTypes, fetchAllRoundTypes } = useRoundTypesStore();
  const { departments, fetchAllDepartments } = useDepartmentStore();

  // Get inspector role IDs
  const inspectorRoleIds = useMemo(() => {
    return (rolesData || [])
      .filter((role: rolesObjType) =>
        role.roleName?.toLowerCase().includes("inspector"),
      )
      .map((role: rolesObjType) => role._id)
      .filter((id): id is string => !!id);
  }, [rolesData]);

  // Filter org users that have inspector role IDs
  const inspectorUsers = useMemo(() => {
    if (!orgUsersData || !inspectorRoleIds.length) return [];
    return orgUsersData.filter((user: any) => {
      const userRoleId =
        typeof user.role === "string"
          ? user.role
          : (user.role as any)?._id || (user.role as any)?.$oid;
      return inspectorRoleIds.includes(userRoleId);
    });
  }, [orgUsersData, inspectorRoleIds]);

  useEffect(() => {
    if (!isOpen) return;
    fetchAllRoundTypes();
    fetchChecklists();
    fetchPremises();
    fetchAllRoles();
    fetchOrgUsers();
    fetchAllDepartments();
  }, [
    isOpen,
    fetchPremises,
    fetchAllRoles,
    fetchOrgUsers,
    fetchChecklists,
    fetchAllDepartments,
  ]);

  useEffect(() => {
    if (initialData && (mode === "edit" || mode === "view")) {
      setFormData({
        ...initialData,
        Area: initialData.Area || [],
      });
    } else if (mode === "create") {
      setFormData({
        roundName: "",
        roundType: "",
        Frequency: "",
        Premise: "",
        Area: [],
        assignedRole: "",
        checklistTemplate: "",
        status: "Scheduled",
        sla: "",
        escalationMatrix: "",
      });
    }
    setValidationError("");
  }, [initialData, mode, isOpen]);

  if (!isOpen) return null;

  const isViewMode = mode === "view";

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    if (isViewMode) return;
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAreasChange = (selectedValues: string[]) => {
    if (isViewMode) return;
    setFormData((prev) => ({
      ...prev,
      Area: selectedValues,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.roundType) {
      toast.error("Round Type is required");
      return false;
    }
    if (!formData.Frequency) {
      toast.error("Frequency is required");
      return false;
    }
    if (!formData.Premise) {
      toast.error("Premise is required");
      return false;
    }
    if (!formData.assignedRole) {
      toast.error("Assigned Role is required");
      return false;
    }
    if (!formData.checklistTemplate) {
      toast.error("Checklist Template is required");
      return false;
    }
    if (!formData.sla || isNaN(Number(formData.sla))) {
      toast.error("SLA (hours) is required");
      return false;
    }
    if (Number(formData.sla) <= 0) {
      toast.error("SLA must be greater than 0");
      return false;
    }
    if (!formData.Area || formData.Area.length === 0) {
      toast.error("At least one Area must be selected");
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isViewMode) {
      onClose();
      return;
    }
    if (!validateForm()) return;
    onSubmit?.(formData);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {mode === "edit"
              ? "Edit Round Planning"
              : mode === "view"
                ? "View Round Planning"
                : "Add Round Planning"}
          </h2>
        </div>

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Round Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Round Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="roundName"
                  value={formData.roundName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required={!isViewMode}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Round Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Round Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="roundType"
                  value={formData.roundType}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required={!isViewMode}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Round Type</option>
                  {roundTypes.map((type, index) => (
                    <option key={index} value={type._id}>
                      {type.roundType}
                    </option>
                  ))}
                </select>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Frequency <span className="text-red-500">*</span>
                </label>
                <select
                  name="Frequency"
                  value={formData.Frequency}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required={!isViewMode}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Frequency</option>
                  {frequencyOptions.map((freq) => (
                    <option key={freq} value={freq}>
                      {freq}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Premise <span className="text-red-500">*</span>
                </label>
                <select
                  name="Premise"
                  value={formData.Premise}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required={!isViewMode}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Premise</option>
                  {allPremises?.map((premise: any) => (
                    <option key={premise._id} value={premise._id}>
                      {premise.premisesName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Areas multi-select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Area(s) <span className="text-red-500">*</span>
                </label>
                {isViewMode ? (
                  <div className="w-full rounded-md p-2 mt-1 border bg-gray-50 max-h-[80px] overflow-y-auto">
                    {Array.isArray(formData.Area) &&
                    formData.Area.length > 0 ? (
                      <div className="space-y-1">
                        {formData.Area.map((area: string, index: number) => (
                          <div
                            key={index}
                            className="text-sm flex items-center gap-2"
                          >
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            {area}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <span className="w-2 h-2 rounded-full bg-gray-300"></span>
                        No areas selected
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className={
                      validationError &&
                      (!formData.Area || formData.Area.length === 0)
                        ? "border-red-500 border-solid border-2 rounded-lg"
                        : ""
                    }
                  >
                    <MultiSelectDropdown
                      options={departments.map((department: any) => ({
                        label: department.departmentName,
                        value: department._id,
                      }))}
                      selectedValues={
                        Array.isArray(formData.Area) ? formData.Area : []
                      }
                      onChange={handleAreasChange}
                      placeholder="Select Area(s)"
                      containerClassName="bg-white"
                    />
                  </div>
                )}
              </div>

              {/* Assigned Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Role (Inspector){" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  name="assignedRole"
                  value={formData.assignedRole}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required={!isViewMode}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Inspector User</option>
                  {inspectorUsers.map((user: any) => {
                    const userName =
                      user.name ||
                      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                      user.email ||
                      "Unknown User";
                    const userId =
                      user._id || (user._id as any)?.$oid || user.id;
                    return (
                      <option key={userId} value={userId}>
                        {userName} {user.email ? `(${user.email})` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Checklist Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Checklist Template <span className="text-red-500">*</span>
                </label>
                <select
                  name="checklistTemplate"
                  value={formData.checklistTemplate}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required={!isViewMode}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Checklist Template</option>
                  {checklists.map((template) => (
                    <option key={template._id} value={template._id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* SLA */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SLA (Completion Time in Hours){" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="sla"
                  value={formData.sla}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required={!isViewMode}
                  min={1}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  name="status"
                  value={formData.status || "Scheduled"}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required={!isViewMode}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              {/* Escalation Matrix (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Escalation Matrix (Optional)
                </label>
                <textarea
                  name="escalationMatrix"
                  value={formData.escalationMatrix || ""}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  rows={3}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-2 bg-[#61748e] border-t border-gray-200 flex justify-end items-center gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center font-semibold gap-3 bg-[#869ab6] text-white px-4 py-2 rounded-full"
          >
            CANCEL
          </button>
          {isViewMode ? null : (
            <button
              type="submit"
              onClick={handleSubmit}
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
            >
              {mode === "edit" ? "UPDATE" : "SUBMIT"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddRoundPlanning;
